/**
 * Reveal resolution — web mirror of the API `form-schema/reveal-resolution.ts`
 * (027-member-message-compose US3, FR-005/FR-006). Given a topic's fields + the
 * member's current values, computes the active path: the visible field set
 * (top-level + the follow-ups revealed by the active selections, recursively)
 * and the active guidance dead-end if reached. Pure.
 *
 * `WizardQuestionFlow` uses it to project the active path to one-question
 * steps, to validate only the visible required fields, to route dead-ends to
 * the terminal-guidance step (FR-006/FR-027), and to filter the outbound
 * payload to the visible fields. Web values are scalar strings (one per
 * field name).
 */

import type { Field, GuidanceDeadEnd } from './MessageComposePort.js';

export interface ActivePath {
  readonly visibleFields: Field[];
  readonly deadEnd: GuidanceDeadEnd | null;
}

/** A `ConditionalRule.reveal` is a dead-end when it is NOT a `Field[]`. */
export function isGuidanceDeadEnd(
  reveal: readonly Field[] | GuidanceDeadEnd,
): reveal is GuidanceDeadEnd {
  return !Array.isArray(reveal);
}

/** Find the active reveal rule for a field given the current value, if any. */
export function activeRule(
  field: Field,
  values: Readonly<Record<string, string>>,
): { reveal: readonly Field[] | GuidanceDeadEnd } | undefined {
  if (field.reveals === undefined) {
    return undefined;
  }
  const value = values[field.name];
  if (value === undefined || value.length === 0) {
    return undefined;
  }
  return field.reveals.find((rule) => rule.whenValue === value);
}

/** Walk the reveal tree against `values`, collecting visible fields + the dead-end. */
export function resolveActivePath(
  fields: readonly Field[],
  values: Readonly<Record<string, string>>,
): ActivePath {
  const visibleFields: Field[] = [];
  let deadEnd: GuidanceDeadEnd | null = null;
  for (const field of fields) {
    visibleFields.push(field);
    const rule = activeRule(field, values);
    if (rule === undefined) {
      continue;
    }
    if (isGuidanceDeadEnd(rule.reveal)) {
      deadEnd = rule.reveal;
    } else {
      const sub = resolveActivePath(rule.reveal, values);
      visibleFields.push(...sub.visibleFields);
      if (sub.deadEnd !== null) {
        deadEnd = sub.deadEnd;
      }
    }
  }
  return { visibleFields, deadEnd };
}
